import React, { FC, Fragment, LegacyRef, useEffect } from 'react';
import { Collapse } from 'bootstrap';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Container } from '@components/atoms/Container';
import { MenuItemLink } from './MenuItemLink';
import { MenuItemWithSubItem } from './MenuItemWithSubItem';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { MenuItem, VisibleSubLinks } from '@types';
import styles from './Menu.module.css';
import { useThemes } from '@hooks/useThemes';
import { useData } from '@hooks/useData';

export interface MenuProps {
    offsetTop: number;
    menuRef?: LegacyRef<HTMLDivElement>;
    menuData: MenuItem[];
    mainLinksToShow: string[];
    setMainLinksToShow: (items: string[]) => void;
    activeItem: string | null;
    setActiveItem: (item: string | null) => void;
    showSubcategoriesOf: VisibleSubLinks;
    setShowSubcategoriesOf: (value: VisibleSubLinks) => void;
    closeSidebar: () => void;
}

export const Menu: FC<MenuProps> = ({
    offsetTop,
    menuRef,
    menuData,
    mainLinksToShow,
    setMainLinksToShow,
    activeItem,
    setActiveItem,
    showSubcategoriesOf,
    setShowSubcategoriesOf,
    closeSidebar,
}) => {
    const { useGrayscaleTheme } = useThemes();
    const pageSize = useMediaQuery();
    const isMobile = ['xs', 'sm'].includes(pageSize);

    const { allSettings } = useData();
    const data = allSettings?.data;

    useEffect(() => {
        if (isMobile) {
            if (showSubcategoriesOf !== null) {
                const elemToHide = document.getElementById(`menu-accordion-${showSubcategoriesOf.menuItemId}`);
                if (elemToHide) {
                    const collapseInstance = Collapse.getOrCreateInstance(elemToHide);
                    collapseInstance.show();
                }
            } else if (activeItem !== null) {
                const elemToShow = document.getElementById(`menu-accordion-${activeItem}`);
                if (elemToShow) {
                    const collapseInstance = Collapse.getOrCreateInstance(elemToShow);
                    collapseInstance.show();
                }
            }
        } else {
            // when viewport transitions to desktop,
            // only display active element
            activeItem ? setMainLinksToShow([activeItem]) : closeSidebar();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile]);

    useEffect(() => {
        // close menu items
        menuData.forEach((item) => {
            if (item.unCollapses && item.id !== activeItem) {
                const elemToHide = document.getElementById(`menu-accordion-${item.id}`);
                if (elemToHide) {
                    const collapseInstance = Collapse.getOrCreateInstance(elemToHide);
                    collapseInstance.hide();
                }
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeItem]);

    const logoItem = data?.layouts?.find((item) => item.name === 'logo');
    const logoSrc = logoItem?.value;

    return (
        <div
            ref={menuRef || undefined}
            id={styles['collapseMultilevelMenu']}
            className={
                // theme
                `${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
                // other classes
                ' collapse collapse-horizontal'
            }
            style={{ top: offsetTop }}
            data-testid="collapseMultilevelMenu">
            <div className={styles.background}></div>
            <div className={`${styles.content} ${showSubcategoriesOf ? `${styles['showing-subcategories']}` : ''}`}>
                <div className={`sidebar-close ${styles['sidebar-close']}`}>
                    <Container classes={styles.container}>
                        <>
                            {/* logo */}
                            <Anchor href="" mode={AnchorMode.none}>
                                <img
                                    className="d-none d-md-block brand-logo"
                                    src={logoSrc}
                                    alt={allSettings?.data?.marketplaceName}
                                />
                            </Anchor>
                            <button onClick={closeSidebar}>
                                <i className="fa-light fa-xmark" />
                            </button>
                        </>
                    </Container>
                </div>
                <div className="d-flex flex-column flex-grow-1 p-md-0">
                    <div className="navbar-nav flex-grow-1">
                        <div className={`accordion w-100 multilevel-menu ${styles['multilevel-menu']}`}>
                            {menuData.map((item) => (
                                <Fragment key={`menu-item-${item.id}`}>
                                    {item.linkTo ? (
                                        <MenuItemLink
                                            id={item.id}
                                            label={item.label}
                                            linkTo={item.linkTo}
                                            show={
                                                isMobile
                                                    ? showSubcategoriesOf === null
                                                    : mainLinksToShow.includes(item.id) && showSubcategoriesOf === null
                                            }
                                        />
                                    ) : (
                                        <MenuItemWithSubItem
                                            item={item}
                                            show={mainLinksToShow.includes(item.id) || isMobile}
                                            activeItem={activeItem}
                                            setActiveItem={setActiveItem}
                                            showSubcategoriesOf={showSubcategoriesOf}
                                            setShowSubcategoriesOf={setShowSubcategoriesOf}
                                            closeSidebar={closeSidebar}
                                        />
                                    )}
                                </Fragment>
                            ))}
                        </div>
                    </div>
                    <SocialMedia />
                </div>
            </div>
        </div>
    );
};

const SocialMedia: FC = () => {
    const { allSettings } = useData();

    return (
        <div className={styles['social-media']}>
            <div className="d-grid d-md-none py-5 px-2">
                <ul className="nav d-inline-flex justify-content-center">
                    {allSettings?.data?.social_networks?.map(
                        (item, index) =>
                            item.url && (
                                <li key={`social-link-${index}`} className={`nav-item ${styles['nav-item']} px-2`}>
                                    <Anchor
                                        href={item?.url}
                                        mode={AnchorMode.none}
                                        classes={`nav-link ${styles['nav-link']} p-0 text-body-secondary`}
                                        ariaLabel={item?.name}>
                                        <RoundIcon icon={item?.icon} variant={RoundIconVariants.variant1}></RoundIcon>
                                    </Anchor>
                                </li>
                            )
                    )}
                </ul>
            </div>
        </div>
    );
};
